import { isEqual } from 'lodash';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import {
  addEdges,
  deleteEdges,
  getDependentEdges,
  getIsNodeEditorReadOnly,
  updateNodeData,
} from 'modules/workflows/ducks';
// import { AbSplitNodeData } from 'modules/workflows/types';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CHECKBOX_STATUS, Checkbox } from 'ui/checkbox';
import { FormSectionContent } from '../../EditorBody.styled';
import { AddSplits, SplitPercentageInput } from '../../editorComponents/SplitEditors';
import {
  InvalidSplits,
  SplitList,
  SplitTitle,
} from '../../editorComponents/SplitEditors/SplitEditor.style';
import { useABSplitEdgeRemapper } from '../../editorComponents/utils/useEdgeRemapper';
// import { EditorBaseProps } from '../../types';
import {
  AB_SPLIT_EDITOR_NAME,
  EVENLY_DISTRIBUTE_TEST_ID,
  INVALID_SPLIT_GROUPS_TEST_ID,
} from './ABSplitEditor.constants';
import { CheckboxLabel, EvenlyDistribute } from './ABSplitEditor.styled';
import { useEvenlyDistribute } from './useEvenlyDistribute';
import { useSplitGroups } from './useSplitGroups';
import { useSplitGroupsValidation } from './useSplitGroupsValidation';
import { splitGroupsToNodeData, splitPercentageToString } from './utils';

// export type ABSplitEditorProps = EditorBaseProps<AbSplitNodeData>;

export const ABSplitEditor = ({ nodeData, nodeId, ...rest } /*: ABSplitEditorProps */) => {
  const edges = useSelector((state) => getDependentEdges(state, nodeId));
  const isReadOnly = useSelector(getIsNodeEditorReadOnly);
  const [evenlyDistribute, toggleEvenlyDistribute] = useEvenlyDistribute();

  const { splitGroups, onAdd, onRemove, onUpdate } = useSplitGroups(
    nodeData?.splitGroups,
    evenlyDistribute,
  );

  const [remappedEdges, { removeEdgeAt }] = useABSplitEdgeRemapper(edges);

  const dispatch = useDispatch();

  const isValid = useSplitGroupsValidation(splitGroups, evenlyDistribute);

  const handleRemove = (index /*: number */) => {
    onRemove(index);
    removeEdgeAt(index);
  };

  const handleUpdate = (requiresValidation = true) => {
    if (!isValid && requiresValidation) return; // do not allow the node to be updated if it is invalid or in draft mode.

    const updatedNodeData = {
      splitGroups: splitGroupsToNodeData(splitGroups),
    };

    dispatch(updateNodeData(updatedNodeData, nodeId));
    dispatch(deleteEdges(edges));
    dispatch(addEdges(remappedEdges));
  };

  const initialNodeData = nodeData?.splitGroups;
  const updatedNodeData = useMemo(() => splitGroupsToNodeData(splitGroups), [splitGroups]);

  function getHasUnsavedChanges() {
    // because the floats of splitPercentage change without user input, we need to compare the string versions
    const stringifiedSplitGroups = (splitGroupsArray) =>
      splitGroupsArray.map((splitGroup) => ({
        ...splitGroup,
        splitPercentage: splitPercentageToString(splitGroup.splitPercentage, 1),
      }));
    const roundedInitialNodeData = stringifiedSplitGroups(initialNodeData);
    const roundedUpdatedNodeData = stringifiedSplitGroups(updatedNodeData);
    return !isEqual(roundedInitialNodeData, roundedUpdatedNodeData);
  }
  return (
    <NodeEditor
      {...rest}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
      getHasUnsavedChanges={getHasUnsavedChanges}
    >
      <SplitTitle as="h3">Percentages</SplitTitle>
      <FormSectionContent>
        <SplitList>
          {splitGroups.map((splitGroup, i) => (
            <SplitPercentageInput
              splitEditor={AB_SPLIT_EDITOR_NAME}
              key={i}
              hasError={!isValid}
              value={splitGroup.splitPercentage}
              isDisabled={isReadOnly || evenlyDistribute === CHECKBOX_STATUS.CHECKED}
              itemIndex={i}
              onChange={(val) => onUpdate(val, i)}
              showRemove={splitGroups.length > 2}
              onRemove={handleRemove}
            />
          ))}
          {!isValid && (
            <InvalidSplits data-test={INVALID_SPLIT_GROUPS_TEST_ID}>
              Percentages must add up to 100%
            </InvalidSplits>
          )}
        </SplitList>
        {!isReadOnly && splitGroups.length < 10 && (
          <AddSplits
            splitEditor={AB_SPLIT_EDITOR_NAME}
            text="Add another split"
            toolTipText="You can add up to 10 splits per A/B node."
            onClick={onAdd}
          />
        )}
        {!isReadOnly && (
          <EvenlyDistribute>
            <Checkbox
              id={EVENLY_DISTRIBUTE_TEST_ID}
              status={evenlyDistribute}
              onClick={toggleEvenlyDistribute}
              data-test={EVENLY_DISTRIBUTE_TEST_ID}
            />
            <CheckboxLabel onClick={toggleEvenlyDistribute}>
              Distribute contacts evenly across branches
            </CheckboxLabel>
          </EvenlyDistribute>
        )}
      </FormSectionContent>
    </NodeEditor>
  );
};
